import { useMutation } from '@tanstack/react-query';
import { IEmbedPreviewV2Payload, IEmbedPreviewV3Payload } from '@opengov/reporting-types';
import { preview as previewSDK } from '@opengov/reporting-embeds';

import { useToast } from '../../../../stories/components/Toast/context/useToast.context';
import { embedPickerQueryKeys } from './embedPickerQueryKeys';

export type PreviewEmbedVariables = IEmbedPreviewV2Payload | IEmbedPreviewV3Payload;

// Preview results are ephemeral — nothing is written to the TanStack cache.
// AbortError is swallowed (user navigated away / requested a new preview).
//
// Why three checks?
// 1. DOMException { name: 'AbortError' } — standard fetch abort (no reason / Error reason)
// 2. DOMException.ABORT_ERR code (20) — some transports rethrow losing the name property
// 3. typeof string — when abort(reasonString) is called with a plain string reason,
//    wretch propagates the string directly as the rejection value rather than a DOMException.
//    Any string rejection from previewSDK is always an abort; real errors are Error objects.
function isAbortError(error: unknown): boolean {
  if (typeof error === 'string') return true;
  if (!error || typeof error !== 'object') return false;
  const e = error as { name?: string; code?: number };
  return e.name === 'AbortError' || e.code === DOMException.ABORT_ERR;
}

export function usePreviewEmbed() {
  const { toggleToast } = useToast();

  return useMutation<any, Error, PreviewEmbedVariables>({
    mutationKey: [...embedPickerQueryKeys.all, 'previewEmbed'],
    mutationFn: (payload) => previewSDK(payload),
    onError: (error) => {
      if (!isAbortError(error)) {
        toggleToast({ title: 'Could not generate embed preview', severity: 'error' });
      }
    }
  });
}
